/** @format */

// File: src/components/relays/RelayManager.jsx
// Description: Main relay declaration container component managing local data states without hard browser side effects.
// Version: 3.2.9

import { useState, useEffect } from "@wordpress/element";
import Swal from "sweetalert2";
import RelaySquadList from "./RelaySquadList.jsx";
import RelayEventSelector from "./RelayEventSelector.jsx";
import RosterOrderConfigModal from "./RosterOrderConfigModal.jsx";

const RelayManager = ({ meetingPostId, ageStandard = "standard_intl", registrationDeadline, onDataMutation, meetingSchedule = [] }) => {
    const [relayEvents, setRelayEvents] = useState([]);
    const [clubRoster, setClubRoster] = useState([]);
    const [declaredTeams, setDeclaredTeams] = useState([]);
    const [activeEvent, setActiveEvent] = useState(null);
    const [activeTeamId, setActiveTeamId] = useState(null);
    const [loading, setLoading] = useState(true);

    const isDeadlinePassed = () => {
        if(!registrationDeadline) return false;
        return new Date().getTime() >= new Date(registrationDeadline).getTime();
    };

    const loadRelayWorkspaceData = () => {
        const globalData = window.ammData;
        if(!globalData?.api_url || !meetingPostId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        const scheduleUrl = `${ globalData.api_url }/meetings/${ meetingPostId }/relay-entries`;
        const rosterUrl = `${ globalData.api_url }/athletes?context=club_roster`;

        return Promise.all([
            fetch(scheduleUrl, { headers: { "X-WP-Nonce": globalData.nonce } }).then(res => res.json()),
            fetch(rosterUrl, { headers: { "X-WP-Nonce": globalData.nonce } }).then(res => res.json())
        ])
            .then(([teamsData, rosterData]) => {
                if(Array.isArray(rosterData)) setClubRoster(rosterData);
                if(Array.isArray(teamsData)) setDeclaredTeams(teamsData);
            })
            .catch(err => {
                console.error("Workspace synchronization exception:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        const globalData = window.ammData;
        if(Array.isArray(meetingSchedule) && meetingSchedule.length > 0) {
            const relaysOnly = meetingSchedule.filter(item => {
                const title = (item.disciplineName || "").toLowerCase();
                return title.includes("relay") || title.includes("4x");
            });
            setRelayEvents(relaysOnly);
            if(relaysOnly.length > 0) {
                setActiveEvent(relaysOnly[0]);
            }
        } else if(globalData?.api_url && meetingPostId) {
            fetch(`${ globalData.api_url }/meetings/${ meetingPostId }/schedule?type=relays`, {
                headers: { "X-WP-Nonce": globalData.nonce }
            })
                .then(res => res.json())
                .then(data => {
                    if(Array.isArray(data)) {
                        setRelayEvents(data);
                        if(data.length > 0) setActiveEvent(data[0]);
                    }
                })
                .catch(err => console.error("Schedule retrieval error:", err));
        }
    }, [meetingSchedule, meetingPostId]);

    useEffect(() => {
        loadRelayWorkspaceData();
    }, [meetingPostId]);

    const handleCreateRelayTeam = () => {
        if(!activeEvent || isDeadlinePassed()) return;

        const globalData = window.ammData;
        fetch(`${ globalData.api_url }/meetings/${ meetingPostId }/submit-relay-team`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-WP-Nonce": globalData.nonce },
            body: JSON.stringify({
                scheduleId: activeEvent.id,
                disciplineId: activeEvent.disciplineId,
                ageClass: activeEvent.ageClass,
                sexCode: activeEvent.sexCode
            })
        })
            .then((res) => res.json())
            .then((data) => {
                if(data.success && data.teamId) {
                    const newTeamItem = {
                        id: data.teamId,
                        meetingPostId: Number(meetingPostId),
                        scheduleId: activeEvent.id,
                        disciplineId: activeEvent.disciplineId,
                        disciplineName: activeEvent.disciplineName,
                        ageClass: activeEvent.ageClass,
                        sexCode: activeEvent.sexCode,
                        status: "Draft",
                        runners: [null, null, null, null, null, null, null, null]
                    };

                    setDeclaredTeams(prev => [...prev, newTeamItem]);
                    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Relay Team Declared", showConfirmButton: false, timer: 2000 });
                } else {
                    Swal.fire("Error", data.message || "Could not declare team", "error");
                }
            })
            .catch((err) => {
                console.error("Critical submission failure:", err);
                Swal.fire("Error", "Routing communication link error", "error");
            });
    };

    return (
        <div className="container-fluid px-0">
            <div className="row g-4">
                <div className="col-md-8 col-lg-9">
                    <RelaySquadList
                        activeEvent={ activeEvent }
                        declaredTeams={ declaredTeams }
                        setDeclaredTeams={ setDeclaredTeams }
                        clubRoster={ clubRoster }
                        isDeadlinePassed={ isDeadlinePassed }
                        onOpenLegConfig={ (teamId) => setActiveTeamId(teamId) }
                        onCreateTeam={ handleCreateRelayTeam }
                    />
                </div>
                <div className="col-md-4 col-lg-3">
                    <RelayEventSelector
                        relayEvents={ relayEvents }
                        activeEvent={ activeEvent }
                        setActiveEvent={ setActiveEvent }
                        declaredTeams={ declaredTeams }
                    />
                </div>
            </div>

            { activeTeamId && (
                <RosterOrderConfigModal
                    teamId={ activeTeamId }
                    team={ declaredTeams.find(t => t.id === activeTeamId) }
                    activeEvent={ activeEvent }
                    clubRoster={ clubRoster }
                    isDeadlinePassed={ isDeadlinePassed }
                    onClose={ () => {
                        // Mirroring AthleteManager: Reset selection states directly to prevent layout refreshes
                        setActiveTeamId(null);
                    } }
                    setDeclaredTeams={ setDeclaredTeams }
                />
            ) }
        </div>
    );
};

export default RelayManager;